import React from "react";
import { Space, Typography } from "antd";
import { DependencyStep, EditDiff, hasEdit, plural } from "./dependencyActions";

const { Text } = Typography;

/** « Prénom Nom », or the e-mail when the name is empty. */
export const responsableLabel = r =>
  [r.firstname, r.lastname].filter(Boolean).join(" ") ||
  r.email ||
  "(sans nom)";

const institutionOf = r => (r.institution && r.institution.name) || "?";

const FIELDS = [
  { key: "firstname", label: "Prénom", kind: "text" },
  { key: "lastname", label: "Nom", kind: "text" },
  { key: "email", label: "E-mail", kind: "text" },
  { key: "phone", label: "Téléphone", kind: "text" },
  { key: "role", label: "Rôle", kind: "text" },
];

/** « Responsable (avant) »: name, role, e-mail, phone, institution, mentions. */
const renderBefore = r => (
  <>
    <Text strong>{responsableLabel(r)}</Text>
    {r.role && <Text type="secondary">{r.role}</Text>}
    {r.email && <Text type="secondary">{r.email}</Text>}
    {r.phone && <Text type="secondary">{r.phone}</Text>}
    <Text type="secondary">
      institution : {institutionOf(r)}
      {r.institution && r.institution.isTarget
        ? " (institution qui reçoit la fusion)"
        : ""}
    </Text>
    <Text type="secondary">
      mentionné·e dans{" "}
      {plural(r.prestationsMentioning, "prestation", "prestations")}
    </Text>
  </>
);

/** « Après »: institution = result, or « fusionné·e dans … », plus the edits. */
const renderAfter = (r, d, ctx) => {
  if (d.action === "merge") {
    const s = d.into && ctx.byId.get(d.into);
    if (!s) return <Text type="danger">choisir le responsable qui reste</Text>;
    return (
      <Space direction="vertical" size={2}>
        <Text>
          fusionné·e dans : <Text strong>{responsableLabel(s)}</Text>
        </Text>
        <Text type="secondary">
          supprimé·e — les prestations gardent leur copie du responsable
        </Text>
      </Space>
    );
  }
  // items are the sources' (+ existing target's) responsables: they all end up on the result
  const moves =
    !r.institution ||
    r.institution.isTarget ||
    ctx.cluster.has(String(r.institution._id));
  const losers = [...ctx.byId.values()].filter(
    x => x._id !== r._id && ctx.map[x._id] && ctx.map[x._id].into === r._id,
  );
  return (
    <Space direction="vertical" size={2}>
      <Text>
        institution :{" "}
        <Text strong>{moves ? ctx.targetName : institutionOf(r)}</Text>
      </Text>
      {losers.length > 0 && (
        <Text type="secondary">
          remplace : {losers.map(l => responsableLabel(l)).join(", ")}
        </Text>
      )}
      {hasEdit(d) && <EditDiff fields={FIELDS} original={r} edit={d.edit} />}
    </Space>
  );
};

const SPEC = {
  key: "responsables",
  idsKey: "responsableIds",
  alert:
    "Chaque responsable des établissements fusionnés est listé avec son état aujourd'hui (avant), ce qu'il deviendra (après) et l'action appliquée. Les doublons détectés exigent une décision : garder séparés ou fusionner.",
  note:
    "Les prestations gardent la copie du responsable saisie à l'époque ; fusionner un doublon ne réécrit pas l'historique.",
  empty: "Aucun responsable concerné.",
  noOthers: "Tous les responsables concernés font partie d'un doublon.",
  groupTitle: (g, n) =>
    `Doublon probable : ${g.label} — ${plural(
      n,
      "responsable",
      "responsables",
    )}`,
  othersTitle: n => `Autres responsables — ${n}`,
  beforeTitle: "Responsable (avant)",
  radioKeep: "Rattacher tel·le quel·le",
  radioMerge: "Fusionner dans →",
  radioEdit: "Modifier",
  selectPlaceholder: "Choisir le responsable qui reste",
  alreadyMerged: "déjà fusionné·e",
  noCandidate: "aucun autre responsable",
  chainNote: "ce responsable est lui-même fusionné — choisissez-en un autre",
  labelOf: responsableLabel,
  ownerOf: institutionOf,
  editFields: () => FIELDS,
  candidates: (r, all) => all,
  fallbackItems: null,
  renderBefore,
  renderAfter,
};

/**
 * Step « 4. Responsables » — every responsable of the merged institutions, before → after,
 * with its action: keep, merge into another one (the duplicate is deleted, the prestations
 * keep their embedded copy), or edit. Duplicate groups require an explicit decision.
 */
export function StepResponsables(props) {
  return <DependencyStep spec={SPEC} {...props} />;
}
